import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Checkbox,
  Skeleton,
  Stack,
  Typography,
  TypographyProps,
  type AccordionProps,
  type StackProps,
} from '@mui/material'
import React, { FC, PropsWithChildren, createContext, useContext, useMemo } from 'react'
import { pickChildrenByType } from '../../utils/childrenPickers'
import {
  useAccordionSelection,
  SelectableItem,
  UseAccordionSelectionReturn,
} from './hooks/useAccordionSelection'

interface AccordionSelectionContextType {
  selection: UseAccordionSelectionReturn | null
}

const AccordionSelectionContext = createContext<AccordionSelectionContextType>({
  selection: null,
})

interface TitleProps extends StackProps {
  children: React.ReactNode
  enableSelection?: boolean
}

const Title = ({ children, enableSelection = false, ...props }: TitleProps) => {
  const { selection } = useContext(AccordionSelectionContext)

  return (
    <Stack
      alignItems="center"
      direction="row"
      spacing={1}
      sx={{ minWidth: 'fit-content' }}
      {...props}
    >
      {enableSelection && selection && (
        <Checkbox
          checked={selection.selectAll}
          size="small"
          onChange={e => selection.handleSelectAll(e.target.checked)}
          onClick={e => e.stopPropagation()}
        />
      )}
      <Typography component="h6" sx={{ fontWeight: 600, flexShrink: 0 }} variant="subtitle1">
        {children}
      </Typography>
    </Stack>
  )
}

const Description = ({ children, ...props }: { children: React.ReactNode } & TypographyProps) => {
  return (
    <Typography color="text.secondary" variant="body2" {...props}>
      {children}
    </Typography>
  )
}

interface ItemCheckProps {
  itemId: string
}

const ItemCheck: FC<ItemCheckProps> = ({ itemId }) => {
  const { selection } = useContext(AccordionSelectionContext)

  if (!selection) {
    return null
  }

  return (
    <Checkbox
      checked={selection.isItemSelected(itemId)}
      size="small"
      sx={{ p: 0.5 }}
      onChange={e => selection.handleItemSelection(itemId, e.target.checked)}
      onClick={e => e.stopPropagation()}
    />
  )
}

type AccordionItemProps = FC<PropsWithChildren<StackProps>> & {
  Check: FC<ItemCheckProps>
}

export const AccordionItem: AccordionItemProps = ({ children, ...props }) => {
  return (
    <Stack direction="row" spacing={1} {...props}>
      {children}
    </Stack>
  )
}

AccordionItem.Check = ItemCheck

interface MonitoringAccordionProps<T extends SelectableItem> extends AccordionProps {
  isLoading?: boolean
  items?: T[]
  onItemsChange?: (items: T[]) => void
  getItemId?: (item: T) => string
  enableSelection?: boolean
}

export const MonitoringAccordion = <T extends SelectableItem>({
  isLoading,
  children,
  items,
  onItemsChange,
  getItemId,
  enableSelection = false,
  ...props
}: PropsWithChildren<MonitoringAccordionProps<T>>) => {
  const titleElements = pickChildrenByType(children, Title)
  const descriptionElements = pickChildrenByType(children, Description)
  const itemElements = pickChildrenByType(children, AccordionItem)

  const selection = useAccordionSelection({
    items: items || [],
    onItemsChange: onItemsChange || (() => {}),
    getItemId:
      getItemId || ((item: T) => (item.properties?.id || item.properties?.tmpId || '').toString()),
  })

  const contextValue = useMemo(
    () => ({
      selection: enableSelection ? selection : null,
    }),
    [enableSelection, selection],
  )

  return (
    <AccordionSelectionContext.Provider value={contextValue}>
      <Accordion sx={{ width: '100%' }} {...props}>
        <AccordionSummary
          aria-controls="panel-content"
          expandIcon={<ExpandMoreIcon />}
          sx={{
            '& .MuiAccordionSummary-content': {
              display: 'flex',
              gap: 3,
              alignItems: 'center',
            },
          }}
        >
          {titleElements}
          {descriptionElements}
        </AccordionSummary>
        <AccordionDetails>
          {isLoading ? <Skeleton height={100} variant="rectangular" width="100%" /> : itemElements}
        </AccordionDetails>
      </Accordion>
    </AccordionSelectionContext.Provider>
  )
}

MonitoringAccordion.Title = Title
MonitoringAccordion.Description = Description
MonitoringAccordion.Item = AccordionItem
